import { useCallback, useEffect, useState } from "react";
import {
  Send, Loader2, WifiOff, CheckCircle2, AlertCircle, CalendarClock,
} from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import Button from "@/components/ui/Button";
import Switch from "@/components/ui/Switch";
import RecipientsField from "@/components/dashboard/RecipientsField";
import { cn } from "@/lib/cn";
import { useApiData } from "@/hooks/useApiData";
import { getUsageReport, updateUsageReport, sendUsageReport } from "@/api/company";
import { formatZonedDateTime } from "@/lib/dates";
import { formatUsd } from "@/lib/format";
import { commitPending, validateRecipientList } from "@/lib/recipients";

const FALLBACK_FREQUENCIES = [
  { value: "daily", label: "Daily", days: 1 },
  { value: "weekly", label: "Weekly", days: 7 },
  { value: "monthly", label: "Monthly", days: 30 },
  { value: "annually", label: "Annually", days: 365 },
];

const DEMO = {
  config: {
    enabled: true, frequency: "weekly", recipients: ["billing@acme.com"],
    last_sent_at: null, period_days: 7, next_run_at: null, next_run_is_immediate: true,
  },
  frequencies: FALLBACK_FREQUENCIES,
  preview: { period_days: 7, total_runs: 128, passed: 104, failed: 24, pass_rate: 81.3, bugs_found: 17, total_input_tokens: 240000, total_output_tokens: 61000, total_cost_usd: 3.42, by_tester: [], by_project: [{ project: "Web app", runs: 90, cost_usd: 2.4 }, { project: "Checkout", runs: 38, cost_usd: 1.02 }] },
  quota: { plan_id: "starter", status: "active", runs: { used: 128, limit: 500 }, projects: { used: 4, limit: 5 }, testers: { used: 3, limit: 5 } },
  email_configured: true,
  email_active: true,
};

function Toast({ type, message, onClose }) {
  if (!message) return null;
  return (
    <div className={cn("mb-5 flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm",
      type === "error" ? "border-rose-200 bg-rose-50/70 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200"
                       : "border-emerald-200 bg-emerald-50/70 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200")}>
      {type === "error" ? <AlertCircle size={15} className="shrink-0" /> : <CheckCircle2 size={15} className="shrink-0" />}
      <span className="flex-1">{message}</span>
      <button type="button" onClick={onClose} className="text-xs font-medium opacity-60 hover:opacity-100">✕</button>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-center dark:border-white/10 dark:bg-white/5">
      <div className="text-xl font-bold text-slate-800 dark:text-white">{value}</div>
      <div className="text-[0.6875rem] uppercase tracking-wider text-slate-400">{label}</div>
    </div>
  );
}

function QuotaBar({ label, used, limit }) {
  const unlimited = !limit || limit >= 999999;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const color = pct < 75 ? "bg-emerald-500" : pct < 100 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="mb-2.5">
      <div className="flex justify-between text-xs">
        <span className="text-slate-600 dark:text-slate-300">{label}</span>
        <span className="text-slate-400">{used.toLocaleString()} / {unlimited ? "∞" : limit.toLocaleString()}</span>
      </div>
      {!unlimited && (
        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-white/10">
          <div className={cn("h-1.5 rounded-full", color)} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

export default function UsageReport() {
  // The frequency being PREVIEWED. null = "whatever is saved", which is what a
  // fresh page load should show; picking one asks the server for that window.
  const [previewFreq, setPreviewFreq] = useState(null);
  const fetcher = useCallback(() => getUsageReport(previewFreq || undefined), [previewFreq]);
  const { data, loading, demo, reload } = useApiData(fetcher, DEMO);
  const [cfg, setCfg] = useState(DEMO.config);
  const [recipient, setRecipient] = useState("");
  const [recipientError, setRecipientError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);
  // When the last successful write landed — drives the "Saved" confirmation
  // that replaced the Save button.
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => { if (data && data.config) setCfg({ ...DEMO.config, ...data.config }); }, [data]);
  // Refetch when the previewed window changes. Skipped on the initial null so a
  // page load makes one request, not two.
  useEffect(() => {
    if (previewFreq === null) return;
    reload();
  }, [previewFreq, reload]);

  const preview = (data && data.preview) || DEMO.preview;
  const quota = (data && data.quota) || DEMO.quota;
  const byProject = (preview && preview.by_project) || [];
  // The template read preview.total_cost_usd.toFixed() and quota.runs.used
  // straight off the response — a null cost or a missing quota block threw and
  // blanked the whole page instead of rendering a dash.
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const emailConfigured = !data || data.email_configured !== false;
  // Configured is not the same as ON: credentials can be loaded while the
  // platform has sending switched off, and only the second one delivers mail.
  const emailActive = !data || data.email_active !== false;
  const emailReady = emailConfigured && emailActive;
  const frequencies = (data && data.frequencies) || FALLBACK_FREQUENCIES;
  // The cadence whose FIGURES are on screen — the server says which window it
  // computed, so the heading can never label the numbers as something else.
  const shownFreq = (data && data.preview_frequency) || cfg.frequency;
  // Who an on-demand send would fall back to when nothing is saved. Served
  // separately by the API so it is never mistaken for a configured recipient.
  const fallback = (data && data.fallback_recipients) || [];

  // Every one of these comes from the server (reporting/usage_report:
  // period_days, next_run_at, schedule_public). The page used to hardcode
  // `frequency === "monthly" ? 30 : 7` and compute the next send from it, which
  // was a second copy of the scheduler's rule — wrong outright for Daily and
  // Annually, and free to drift from the backend for the other two.
  const everyDays = num(cfg.period_days) || num(preview.period_days) || 7;
  // The zone the emailed report stamps itself in, served by the API. Rendering
  // the next-send time in a DIFFERENT zone from the report it refers to is what
  // made the page and the inbox disagree about the same instant.
  const displayTz = (data && data.display_timezone) || "UTC";
  const displayTzLabel = (data && data.display_timezone_label) || "";
  const nextSend = (() => {
    if (!cfg.enabled) return "Not scheduled";
    if (cfg.next_run_is_immediate || !cfg.next_run_at) return "With the next hourly check";
    return formatZonedDateTime(cfg.next_run_at, displayTz, displayTzLabel);
  })();

  // Every change to the schedule persists immediately.
  //
  // These settings used to live in local state until a Save press. A switch that
  // flips the moment you touch it looks like it saved itself, so leaving the page
  // silently reverted it — reported as "the toggle disables itself". Optimistic:
  // the UI shows the new value at once and rolls back if the write fails, so it
  // never claims something the server did not accept.
  const persist = useCallback(async (next, what) => {
    const previous = cfg;
    setCfg(next);
    setBusy(true);
    try {
      await updateUsageReport({
        enabled: next.enabled, frequency: next.frequency, recipients: next.recipients,
      });
      setSavedAt(Date.now());
      if (what) setToast({ type: "success", message: what });
      // Saving a new cadence changes both the preview window and next_run_at, and
      // both are the server's to compute.
      if (next.frequency !== previous.frequency) setPreviewFreq(next.frequency);
      else await reload();
    } catch (e) {
      setCfg(previous);
      setToast({ type: "error", message: (e && e.message) || "Couldn't save that change." });
    } finally { setBusy(false); }
  }, [cfg, reload]);

  // A recipient is required to save ANYTHING here, the switch included — see
  // lib/recipients.validateRecipientList. Checked before the request so an
  // invalid configuration never leaves the page.
  function setEnabled(next) {
    const { list, error } = commitPending(cfg.recipients, recipient);
    if (error) return setRecipientError(error);
    const listError = validateRecipientList(list);
    if (listError) return setRecipientError(listError);
    setRecipient("");
    setRecipientError("");
    persist({ ...cfg, enabled: next, recipients: list },
      next ? "Automatic reports are on." : "Automatic reports are off.");
  }

  function setRecipients(list) {
    setRecipientError("");
    // Removing the last recipient is refused: a stored report that names nobody
    // cannot be delivered, switched on or off.
    const listError = validateRecipientList(list);
    if (listError) return setRecipientError(listError);
    persist({ ...cfg, recipients: list });
  }

  // Every other write goes through here, so a change to the frequency cannot
  // store a configuration the recipient rule would reject either.
  function persistChecked(next, what) {
    const { list, error } = commitPending(next.recipients, recipient);
    if (error) return setRecipientError(error);
    const listError = validateRecipientList(list);
    if (listError) return setRecipientError(listError);
    setRecipient("");
    setRecipientError("");
    persist({ ...next, recipients: list }, what);
  }

  async function sendNow() {
    // A half-typed address is committed before sending, so Send now cannot mail
    // the previous list while a new recipient sits unsaved in the box.
    const { list, error } = commitPending(cfg.recipients, recipient);
    if (error) return setRecipientError(error);
    if (list.length !== cfg.recipients.length) {
      setRecipient("");
      await persist({ ...cfg, recipients: list });
    }
    setSending(true);
    try {
      const res = await sendUsageReport();
      const failed = res.failed || [];
      // "Sent" means the mail server ACCEPTED it, not that it reached an inbox —
      // say so, because the commonest outcome of a successful send that nobody
      // receives is a spam folder, and a bare "sent" sends people hunting in the
      // wrong place. And never report a partial failure as a clean success.
      if (failed.length) {
        setToast({
          type: "error",
          message: `Sent to ${res.sent}, but ${failed.length} failed: ${failed.join(", ")}.`
            + " Ask your platform admin to check the mail logs.",
        });
      } else {
        setToast({
          type: "success",
          message: `Report accepted by the mail server for ${res.sent} recipient${res.sent === 1 ? "" : "s"}.`
            + " If it doesn't arrive within a few minutes, check the spam folder.",
        });
      }
      await reload();
    } catch (e) { setToast({ type: "error", message: (e && e.message) || "Failed to send." }); }
    finally { setSending(false); }
  }

  return (
    <>
      <PageHeader title="Usage Reports" subtitle="Email your team a periodic summary of runs, pass-rate, bugs and AI cost." />
      <Toast {...(toast || {})} onClose={() => setToast(null)} />

      {demo && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={15} /> Showing demo data — backend not reachable.
        </div>
      )}
      {/* Two different blockers with two different fixes: no credentials at all,
          versus credentials loaded but sending switched off platform-wide. Both
          used to read as "isn't configured yet", which sent the admin chasing a
          setting that was already filled in. */}
      {!emailReady && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <AlertCircle size={15} />
          {!emailConfigured
            ? "Email (SMTP) isn't configured yet — ask your platform admin before scheduling."
            : "Email sending is switched off platform-wide — ask your platform admin to turn it on."}
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 size={26} className="animate-spin" /></div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-2">
          {/* Schedule card */}
          <div className="card p-6">
            <h3 className="flex items-center gap-2 text-base font-semibold text-slate-800 dark:text-white"><CalendarClock size={17} className="text-brand-500" /> Schedule</h3>
            <p className="mt-0.5 text-sm text-slate-400">When it&apos;s on, we email this report automatically.</p>

            {/* The shared ON/OFF switch (components/ui/Switch): row text does not
                activate it, the control and its label do. */}
            <Switch
              className="mt-5"
              label="Automatic email report"
              description={cfg.enabled
                ? `On — sent every ${everyDays} day${everyDays === 1 ? "" : "s"}.`
                : "Off — no reports are sent."}
              checked={cfg.enabled}
              disabled={busy}
              onChange={setEnabled}
            />

            <div className="mt-4">
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400">Frequency</p>
              {/* Rendered from the list the API serves, so the picker cannot offer
                  a cadence the backend would reject — and Daily and Annually,
                  which the backend has always accepted, are no longer missing. */}
              <div className="grid grid-cols-2 gap-2">
                {frequencies.map((f) => (
                  <button key={f.value} type="button" disabled={busy}
                    onClick={() => persistChecked({ ...cfg, frequency: f.value }, `Switched to ${f.label.toLowerCase()} reports.`)}
                    className={cn("rounded-lg border px-3 py-2 text-sm font-medium",
                      cfg.frequency === f.value ? "border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300" : "border-slate-200 text-slate-600 dark:border-white/10 dark:text-slate-300")}>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <RecipientsField
                recipients={cfg.recipients}
                onChange={setRecipients}
                pending={recipient}
                onPendingChange={setRecipient}
                error={recipientError}
                onError={setRecipientError}
                disabled={busy}
                emptyHint={fallback.length
                  ? `No recipients yet — Send now would go to ${fallback.join(", ")}.`
                  : "No recipients yet."}
              />
            </div>

            {/* What the card never said: WHEN it fires and WHAT arrives. Both were
                knowable — the scheduler checks hourly and sends once the interval
                has elapsed since the last send — but a reader had only the word
                "Weekly" to go on, which implies a fixed weekday it does not use. */}
            <dl className="mt-5 space-y-2 rounded-xl bg-slate-50 p-4 text-xs dark:bg-white/5">
              <div className="flex justify-between gap-3">
                <dt className="shrink-0 text-slate-400">When</dt>
                <dd className="text-right font-medium text-slate-700 dark:text-slate-200">
                  Every {everyDays} day{everyDays === 1 ? "" : "s"} after the last send, checked hourly
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="shrink-0 text-slate-400">Last sent</dt>
                <dd className="text-right font-medium text-slate-700 dark:text-slate-200">
                  {formatZonedDateTime(cfg.last_sent_at, displayTz, displayTzLabel, "never")}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="shrink-0 text-slate-400">Next</dt>
                <dd className="text-right font-medium text-slate-700 dark:text-slate-200">{nextSend}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="shrink-0 text-slate-400">Goes to</dt>
                <dd className="text-right font-medium text-slate-700 dark:text-slate-200">
                  {cfg.recipients.length
                    ? `${cfg.recipients.length} recipient${cfg.recipients.length === 1 ? "" : "s"}`
                    : "your billing email"}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="shrink-0 text-slate-400">Contains</dt>
                <dd className="text-right font-medium text-slate-700 dark:text-slate-200">
                  Runs, pass rate, bugs found, spend, and a per-project breakdown
                </dd>
              </div>
            </dl>
            {/* The zone the SERVER renders reports in (settings.report_timezone,
                served as display_timezone), not the viewer's own and not UTC —
                so this card shows the reader the same string that reaches their
                inbox. Stored timestamps stay UTC; this is presentation only.
                The label is read from the API rather than written here, so a
                deployment that changes the zone cannot leave this line lying. */}
            <p className="mt-2 text-[0.6875rem] text-slate-400">
              Times are shown in {displayTzLabel || displayTz} — the same timestamp the
              emailed report carries.
            </p>

            <div className="mt-4 flex items-center justify-between gap-3">
              {/* Replaces the Save button: changes persist as they are made, so the
                  job here is to confirm that they did. */}
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
                {busy ? (
                  <><Loader2 size={13} className="animate-spin" /> Saving…</>
                ) : savedAt ? (
                  <><CheckCircle2 size={13} className="text-emerald-500" /> Saved</>
                ) : (
                  "Changes save automatically."
                )}
              </span>
              <Button variant="outline" size="sm" onClick={sendNow} disabled={sending || !emailReady}>
                {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send now
              </Button>
            </div>
          </div>

          {/* Preview card */}
          <div className="card p-6">
            <h3 className="flex flex-wrap items-center gap-2 text-base font-semibold text-slate-800 dark:text-white">
              <span className="capitalize">{shownFreq}</span> preview
              <span className="text-xs font-normal text-slate-400">
                last {num(preview.period_days) || everyDays} day{num(preview.period_days) === 1 ? "" : "s"}
              </span>
            </h3>
            <p className="mt-0.5 text-sm text-slate-400">
              Exactly what the {shownFreq} email contains. Switch the cadence to preview another period.
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat value={num(preview.total_runs).toLocaleString()} label="Runs" />
              <Stat value={`${num(preview.pass_rate)}%`} label="Passed" />
              <Stat value={num(preview.bugs_found).toLocaleString()} label="Bugs" />
              <Stat value={formatUsd(preview.total_cost_usd)} label="AI cost" />
            </div>

            {quota && (
              <div className="mt-5 border-t border-slate-100 pt-4 dark:border-white/10">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Plan usage this month — <span className="capitalize text-slate-500">{(quota.plan_id || "").replace(/_/g, " ")}</span>
                </p>
                <QuotaBar label="Runs" used={num(quota.runs?.used)} limit={num(quota.runs?.limit)} />
                <QuotaBar label="Projects" used={num(quota.projects?.used)} limit={num(quota.projects?.limit)} />
                <QuotaBar label="Seats" used={num(quota.testers?.used)} limit={num(quota.testers?.limit)} />
              </div>
            )}

            {byProject.length > 0 && (
              <div className="mt-5 border-t border-slate-100 pt-4 dark:border-white/10">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">By project</p>
                <div className="space-y-1.5">
                  {byProject.slice(0, 5).map((p) => (
                    <div key={p.project} className="flex items-center justify-between gap-3 text-sm">
                      <span className="min-w-0 truncate text-slate-700 dark:text-slate-200">{p.project}</span>
                      <span className="shrink-0 text-slate-400">{num(p.runs)} runs · {formatUsd(p.cost_usd)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="mt-4 text-xs text-slate-400">
              {num(preview.total_input_tokens).toLocaleString()} input + {num(preview.total_output_tokens).toLocaleString()} output tokens.
              Every email closes with the time it was generated, in UTC.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
